import { useState, useCallback } from 'react'
import { toast } from '../services/toastService'

/**
 * Custom hook for managing async operations with loading, error, and data states
 * Provides consistent error handling and success notifications across the app
 * 
 * @returns {Object} - { loading, error, data, execute, reset }
 * 
 * @example
 * const { loading, error, data, execute } = useAsyncOperation()
 * 
 * const handleSubmit = async () => {
 *   try {
 *     await execute(
 *       () => api.submitForm(formData),
 *       { 
 *         successMessage: t('form.submitSuccess'),
 *         onSuccess: (result) => navigate('/success')
 *       }
 *     )
 *   } catch (error) {
 *     // Error already handled by execute
 *   }
 * }
 */
export function useAsyncOperation() {
  const [state, setState] = useState({
    loading: false,
    error: null,
    data: null
  })

  /**
   * Execute an async function with automatic state management
   * 
   * @param {Function} asyncFn - The async function to execute
   * @param {Object} options - Configuration options
   * @param {Function} options.onSuccess - Callback executed on success
   * @param {Function} options.onError - Callback executed on error
   * @param {string} options.successMessage - Toast message to show on success
   * @param {string} options.errorMessage - Custom error message (overrides default)
   * @param {boolean} options.silent - If true, don't show error toast
   * @param {Function} options.errorTransform - Transform error before displaying
   * 
   * @returns {Promise} - Resolves with data or rejects with error
   */
  const execute = useCallback(async (asyncFn, options = {}) => {
    const {
      onSuccess,
      onError,
      successMessage,
      errorMessage,
      silent = false,
      errorTransform
    } = options

    setState(prev => ({ ...prev, loading: true, error: null }))
    
    try {
      const data = await asyncFn()
      setState({ loading: false, error: null, data })
      
      // Show success message if provided
      if (successMessage) {
        toast.success(successMessage)
      }
      
      // Execute success callback if provided
      if (onSuccess) {
        onSuccess(data)
      }
      
      return data
    } catch (error) {
      // Transform error if transformer provided
      const transformedError = errorTransform ? errorTransform(error) : error
      
      // Extract error message
      const errorMsg = errorMessage ||
                       transformedError.response?.data?.message ||
                       transformedError.response?.data?.error ||
                       transformedError.message ||
                       'An error occurred'
      
      setState({ loading: false, error: errorMsg, data: null })
      
      // Show error toast unless silent mode
      if (!silent) {
        toast.error(errorMsg)
      }
      
      // Execute error callback if provided
      if (onError) {
        onError(transformedError)
        // Error was handled by the callback — don't re-throw (avoids unhandled rejection warnings)
        return
      }
      
      // Re-throw only when caller has no onError handler (so they can catch it themselves)
      throw transformedError
    }
  }, [])

  /**
   * Reset state to initial values
   */
  const reset = useCallback(() => {
    setState({ loading: false, error: null, data: null })
  }, [])

  return {
    loading: state.loading,
    error: state.error,
    data: state.data,
    execute,
    reset
  }
}
